import * as React from 'react';
import { LOOKING_FOR } from '../config';
import { appendLookingFor } from '../utils/looking-for';

// "Got questions? Tap to explore" (design, Steps 3 and 4): a row at the foot of
// the page that opens a set of question chips; a chip shows its answer in a
// card below, and the last chip ("Ask your question") opens a one-line form
// instead. Each tap is written to ?looking_for (see utils/looking-for) so the
// journey is readable from the address; nothing is sent anywhere.
//
// `step`   the number written in front of each entry in ?looking_for
// `gift`   adds the "Gift receipts" chip
// `style`  merged onto the section (Step 3 gives it the scroll runway)
export default function LookingFor({ step, gift, style }) {
  const copy = LOOKING_FOR;
  const [open, setOpen] = React.useState(false);
  const [pick, setPick] = React.useState('');
  const [other, setOther] = React.useState('');
  const [otherSent, setOtherSent] = React.useState(false);
  const answerRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const expandLogged = React.useRef(false);
  const labels = gift ? copy.labels.concat(copy.giftLabels) : copy.labels;

  const record = (entry) => {
    try {
      window.history.replaceState(window.history.state, '', appendLookingFor(window.location.href, step, entry));
    } catch { /* no-op outside a browser */ }
  };
  const reducedMotion = () => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
  };
  // Scrolls just far enough that the answer's bottom edge clears the screen's.
  const reveal = () => requestAnimationFrame(() => {
    const el = answerRef.current;
    if (!el) return;
    const dy = el.getBoundingClientRect().bottom - (window.innerHeight - 24);
    if (dy > 0) window.scrollBy({ top: dy, behavior: reducedMotion() ? 'auto' : 'smooth' });
  });

  const asking = pick === copy.otherLabel && !otherSent;
  const thanks = otherSent && pick === copy.otherLabel;
  const title = thanks ? copy.thanksTitle : (copy.answers[pick] ? pick : '');
  const body = thanks ? copy.thanksBody : (copy.answers[pick] || '');

  // The question box takes focus when it opens.
  React.useEffect(() => {
    if (asking && inputRef.current) inputRef.current.focus({ preventScroll: true });
  }, [asking]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      reveal();
      if (!expandLogged.current) { expandLogged.current = true; record('Expanded'); }
    }
  };
  const choose = (label) => {
    setPick(label);
    if (label === copy.otherLabel) { setOtherSent(false); reveal(); } else { reveal(); record(label); }
  };
  const submit = (e) => {
    e.preventDefault();
    const text = other.trim();
    record(text ? `${copy.otherLabel}: ${text}` : copy.otherLabel);
    setOtherSent(true);
    setOther('');
    reveal();
  };

  return (
    <section className="ec-look" aria-labelledby={`ec-look-title-${step}`} data-mtm-probe="looking-for" style={style}>
      <button type="button" id={`ec-look-title-${step}`} className="ec-look-toggle" onClick={toggle}
        aria-expanded={open} aria-controls={`ec-look-body-${step}`}>
        <span>{copy.title}</span>
        <svg className={`ec-look-chevron ${open ? 'is-open' : ''}`.trim()} aria-hidden="true" width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M3.5 6.5L9 12l5.5-5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div id={`ec-look-body-${step}`} className="ec-look-body">
          <div className="ec-look-chips">
            {labels.concat(copy.otherLabel).map((label) => (
              <button key={label} type="button" className={`ec-look-chip ${pick === label ? 'is-on' : ''}`.trim()}
                aria-pressed={pick === label} data-mtm-looking-for={label} onClick={() => choose(label)}>
                {/* The same words again in the heavier weight, hidden, so the
                    chip is already as wide as it will be when picked. */}
                <span className="ec-look-chip-text">
                  <span>{label}</span>
                  <span aria-hidden="true" className="ec-look-chip-ghost">{label}</span>
                </span>
              </button>
            ))}
          </div>

          {asking && (
            <form className="ec-look-form" onSubmit={submit}>
              <input ref={inputRef} type="text" className="ec-look-input" maxLength={150} value={other}
                onChange={(e) => setOther(e.target.value.slice(0, 150))}
                aria-label={copy.otherPlaceholder} placeholder={copy.otherPlaceholder} data-mtm-looking-for-text="true" />
              <button type="submit" className="ec-look-send">{copy.otherSend}</button>
            </form>
          )}

          <div role="status" aria-live="polite" ref={answerRef} className="ec-look-live">
            {title && (
              <div className="ec-look-answer" data-mtm-looking-for-answer={title}>
                <div className="ec-look-answer-title">{title}</div>
                <div className="ec-look-answer-body">{body}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
